/**
 * <cart-drawer> Web Component
 * Slide-out cart drawer that reacts to:
 *   - cart:open   → opens the drawer
 *   - cart:updated → re-renders items from the new cart JSON
 *
 * Depends on: RA.cart (theme.js), RA.formatMoney (theme.js)
 */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      /* Cache DOM references */
      this.overlay = this.querySelector('.cart-drawer__overlay');
      this.panel = this.querySelector('.cart-drawer__panel');
      this.closeBtn = this.querySelector('.cart-drawer__close');
      this.itemsContainer = this.querySelector('.cart-drawer__items');
      this.emptyState = this.querySelector('.cart-drawer__empty');
      this.footer = this.querySelector('.cart-drawer__footer');
      this.subtotalPrice = this.querySelector('.cart-drawer__subtotal-price');
      this.statusRegion = this.querySelector('.cart-drawer__status');

      /* Bound handlers (needed for add/removeEventListener) */
      this._onCartOpen = this.open.bind(this);
      this._onCartUpdated = this._handleCartUpdated.bind(this);
      this._onKeyDown = this._handleKeyDown.bind(this);
      this._onOverlayClick = this.close.bind(this);
      this._onCloseClick = this.close.bind(this);
    }

    /* ------------------------------------------------------------------
       Lifecycle
       ------------------------------------------------------------------ */
    connectedCallback() {
      document.addEventListener('cart:open', this._onCartOpen);
      document.addEventListener('cart:updated', this._onCartUpdated);

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this._onCloseClick);
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', this._onOverlayClick);
      }

      /* Delegated listeners inside the items container */
      this._bindItemListeners();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:open', this._onCartOpen);
      document.removeEventListener('cart:updated', this._onCartUpdated);
      document.removeEventListener('keydown', this._onKeyDown);

      if (this.closeBtn) {
        this.closeBtn.removeEventListener('click', this._onCloseClick);
      }
      if (this.overlay) {
        this.overlay.removeEventListener('click', this._onOverlayClick);
      }
    }

    /* ------------------------------------------------------------------
       Open / Close
       ------------------------------------------------------------------ */
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeyDown);

      /* Move focus into the drawer (close button) */
      if (this.closeBtn) {
        this.closeBtn.focus();
      }

      this._trapFocus();
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeyDown);

      /* Return focus to the element that opened the drawer */
      if (this._triggerElement && this._triggerElement.focus) {
        this._triggerElement.focus();
      }
    }

    /* ------------------------------------------------------------------
       Keyboard handling
       ------------------------------------------------------------------ */
    _handleKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      /* Tab trapping */
      if (e.key === 'Tab') {
        this._handleTabTrap(e);
      }
    }

    /* ------------------------------------------------------------------
       Focus trapping
       ------------------------------------------------------------------ */
    _trapFocus() {
      /* Store the trigger so we can restore focus later */
      this._triggerElement = document.activeElement;
    }

    _getFocusableElements() {
      return this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    }

    _handleTabTrap(e) {
      var focusable = this._getFocusableElements();
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    /* ------------------------------------------------------------------
       Cart updated handler
       ------------------------------------------------------------------ */
    _handleCartUpdated(e) {
      var cart = e.detail && e.detail.cart;
      if (!cart) return;
      this._renderCart(cart);
    }

    /* ------------------------------------------------------------------
       Render cart contents from JSON
       ------------------------------------------------------------------ */
    _renderCart(cart) {
      var hasItems = cart.item_count > 0;

      /* Toggle empty state vs items + footer */
      if (this.emptyState) {
        this.emptyState.hidden = hasItems;
      }
      if (this.itemsContainer) {
        this.itemsContainer.hidden = !hasItems;
      }
      if (this.footer) {
        this.footer.hidden = !hasItems;
      }

      /* Update subtotal */
      if (this.subtotalPrice) {
        this.subtotalPrice.textContent = RA.formatMoney(cart.total_price);
      }

      /* Rebuild items HTML */
      if (!this.itemsContainer) return;

      if (!hasItems) {
        this.itemsContainer.innerHTML = '';
        this._announceStatus(cart);
        return;
      }

      var removeText = this.getAttribute('data-remove-text') || 'Remove';
      var html = '';
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var line = i + 1; /* Shopify uses 1-based line index */

        var imageHTML = '';
        if (item.image) {
          imageHTML =
            '<img src="' + this._getSizedImageUrl(item.image, '120x') + '" ' +
            'alt="' + this._escapeHTML(item.title) + '" ' +
            'width="60" height="60" loading="lazy">';
        }

        var variantHTML = '';
        if (item.variant_title && item.variant_title !== 'Default Title') {
          variantHTML =
            '<p class="cart-drawer__item-variant">' +
            this._escapeHTML(item.variant_title) +
            '</p>';
        }

        html +=
          '<div class="cart-drawer__item" data-line="' + line + '" data-variant-id="' + item.variant_id + '">' +
            '<div class="cart-drawer__item-image">' + imageHTML + '</div>' +
            '<div class="cart-drawer__item-details">' +
              '<p class="cart-drawer__item-title">' + this._escapeHTML(item.product_title) + '</p>' +
              variantHTML +
              '<p class="cart-drawer__item-price">' + RA.formatMoney(item.final_line_price) + '</p>' +
            '</div>' +
            '<div class="cart-drawer__item-actions">' +
              '<quantity-input class="cart-drawer__quantity">' +
                '<button class="cart-drawer__qty-btn" type="button" data-action="decrease" ' +
                  'aria-label="' + this._escapeHTML(item.title) + '">&minus;</button>' +
                '<input type="number" value="' + item.quantity + '" min="0" max="99" ' +
                  'aria-label="Quantity" class="cart-drawer__qty-input" data-line="' + line + '">' +
                '<button class="cart-drawer__qty-btn" type="button" data-action="increase" ' +
                  'aria-label="' + this._escapeHTML(item.title) + '">+</button>' +
              '</quantity-input>' +
              '<button class="cart-drawer__remove" type="button" data-line="' + line + '" ' +
                'aria-label="' + this._escapeHTML(removeText) + '">' +
                this._escapeHTML(removeText) +
              '</button>' +
            '</div>' +
          '</div>';
      }

      this.itemsContainer.innerHTML = html;
      this._announceStatus(cart);

      /* Re-initialize quantity-input Web Components inside the new HTML.
         The browser auto-upgrades them when innerHTML inserts the tags,
         but only if the custom element is already defined. We call
         connectedCallback manually if needed as a safety net. */
      var quantityInputs = this.itemsContainer.querySelectorAll('quantity-input');
      for (var q = 0; q < quantityInputs.length; q++) {
        if (typeof quantityInputs[q].init === 'function') {
          quantityInputs[q].init();
        }
      }
    }

    /* ------------------------------------------------------------------
       Delegated event listeners for items (remove buttons)
       Uses event delegation so we don't need to rebind after re-render.
       ------------------------------------------------------------------ */
    _bindItemListeners() {
      var self = this;

      /* Remove button delegation */
      this.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('.cart-drawer__remove');
        if (!removeBtn) return;

        var line = parseInt(removeBtn.dataset.line, 10);
        if (!line) return;

        removeBtn.disabled = true;
        try {
          RA.cart.change(line, 0).catch(function (err) {
            console.error('Remove item error:', err);
            removeBtn.disabled = false;
          });
        } catch (err) {
          console.error('Remove item error:', err);
          removeBtn.disabled = false;
        }
      });
    }

    /* ------------------------------------------------------------------
       Accessibility: announce cart status to screen readers
       ------------------------------------------------------------------ */
    _announceStatus(cart) {
      if (!this.statusRegion) return;
      var template = this.getAttribute('data-status-text') || 'Cart updated, {{ count }} items';
      this.statusRegion.textContent = template.replace('{{ count }}', cart.item_count);
    }

    /* ------------------------------------------------------------------
       Helpers
       ------------------------------------------------------------------ */

    /**
     * Attempt to get a sized Shopify image URL.
     * Shopify CDN images support _WIDTHx suffixes before the extension.
     */
    _getSizedImageUrl(url, size) {
      if (!url) return '';
      /* If the URL is already from image_url filter (contains width param), return as-is */
      if (url.indexOf('width=') !== -1) return url;
      /* Try adding size suffix before the extension */
      var match = url.match(/(.+)(\.\w{3,4})(\?.*)?$/);
      if (match) {
        return match[1] + '_' + size + match[2] + (match[3] || '');
      }
      return url;
    }

    /**
     * Basic HTML escaping to prevent XSS when building innerHTML.
     */
    _escapeHTML(str) {
      if (!str) return '';
      var div = document.createElement('div');
      div.appendChild(document.createTextNode(str));
      return div.innerHTML;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
